import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { heroSlides } from '@/mocks/slides';
import type { HeroSlide } from '@/types';

export default function HeroCarousel() {
  const [active, setActive] = useState(0);
  const slides = heroSlides as HeroSlide[];

  const goTo = useCallback((index: number) => {
    setActive((index + slides.length) % slides.length);
  }, [slides.length]);

  useEffect(() => {
    const timer = window.setInterval(() => {
      setActive((prev) => (prev + 1) % slides.length);
    }, 6000);
    return () => window.clearInterval(timer);
  }, [slides.length]);

  const linkFor = (slide: HeroSlide) =>
    slide.linkType === 'drama' ? `/detail/${slide.linkId}` : `/news/${slide.linkId}`;

  return (
    <section className="relative h-[560px] w-full overflow-hidden bg-foreground-950 md:h-[660px]">
      {slides.map((slide, index) => (
        <div
          key={slide.id}
          className={`absolute inset-0 transition-opacity duration-700 ${
            index === active ? 'opacity-100' : 'pointer-events-none opacity-0'
          }`}
        >
          <img
            src={slide.image}
            alt={`${slide.title} 焦点图`}
            className="h-full w-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/90 via-foreground-950/45 to-foreground-950/35"></div>
        </div>
      ))}

      <div className="relative z-10 flex h-full w-full items-end">
        <div className="w-full px-4 pb-16 md:px-8 md:pb-20 lg:px-12">
          <div className="max-w-3xl">
            <span className="inline-flex items-center gap-2 rounded-full bg-primary-500 px-3 py-1 text-xs font-medium text-background-50">
              <i className="ri-fire-line"></i>
              {slides[active].badge}
            </span>
            <h1 className="mt-4 font-heading text-3xl font-bold leading-tight text-background-50 md:text-5xl">
              {slides[active].title}
            </h1>
            <p className="mt-3 max-w-xl text-sm leading-relaxed text-background-200 md:text-base">
              {slides[active].subtitle}
            </p>
            <div className="mt-7 flex flex-wrap items-center gap-3">
              <Link
                to={linkFor(slides[active])}
                className="inline-flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
              >
                <i className="ri-play-circle-line text-lg"></i>
                查看详情
              </Link>
              <Link
                to={slides[active].linkType === 'drama' ? '/category/tv' : '/news'}
                className="inline-flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md border border-background-100/40 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-background-50/10"
              >
                浏览更多
                <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
          </div>
        </div>
      </div>

      <div className="absolute bottom-16 right-4 z-10 flex items-center gap-2 md:bottom-20 md:right-8 lg:right-12">
        <button
          type="button"
          onClick={() => goTo(active - 1)}
          className="flex h-9 w-9 cursor-pointer items-center justify-center rounded-full border border-background-100/40 text-background-50 transition-colors hover:bg-background-50/15"
          aria-label="上一张"
        >
          <i className="ri-arrow-left-s-line text-lg"></i>
        </button>
        <button
          type="button"
          onClick={() => goTo(active + 1)}
          className="flex h-9 w-9 cursor-pointer items-center justify-center rounded-full border border-background-100/40 text-background-50 transition-colors hover:bg-background-50/15"
          aria-label="下一张"
        >
          <i className="ri-arrow-right-s-line text-lg"></i>
        </button>
      </div>

      <div className="absolute bottom-16 left-4 z-10 flex items-center gap-2 md:bottom-20 md:left-8 lg:left-12">
        {slides.map((slide, index) => (
          <button
            key={slide.id}
            type="button"
            onClick={() => goTo(index)}
            className={`h-1.5 cursor-pointer rounded-full transition-all ${
              index === active ? 'w-8 bg-primary-400' : 'w-3 bg-background-100/50'
            }`}
            aria-label={`切换到第 ${index + 1} 张`}
          ></button>
        ))}
      </div>
    </section>
  );
}